export interface LeaseUpRow {
  month: number;
  newLeases: number;
  totalLeases: number;
  pctLeased: number;
  vacantUnits: number;
  pctVacant: number;
}

/**
 * Compute a month-by-month lease-up schedule using ROUNDDOWN distribution.
 *
 * - First row is the construction-end month (0 new leases, 0 total).
 * - avgPerMonth = Math.floor(totalUnits / totalMonths)  (ROUNDDOWN)
 * - Months 1..(totalMonths-1) get avgPerMonth new leases each.
 * - The final month absorbs the remainder so cumulative ties exactly to totalUnits.
 */
export function computeLeaseUpSchedule(
  constructionEndMonth: number,
  totalUnits: number,
  totalMonths: number,
): LeaseUpRow[] {
  const kickoff: LeaseUpRow = {
    month: constructionEndMonth,
    newLeases: 0,
    totalLeases: 0,
    pctLeased: 0,
    vacantUnits: totalUnits,
    pctVacant: totalUnits > 0 ? 100 : 0,
  };

  if (totalMonths <= 0 || totalUnits <= 0) return [kickoff];

  const avgPerMonth = Math.floor(totalUnits / totalMonths);
  const rows: LeaseUpRow[] = [kickoff];
  let cumulative = 0;

  for (let i = 1; i <= totalMonths; i++) {
    const isLast = i === totalMonths;
    const newLeases = isLast
      ? totalUnits - avgPerMonth * (totalMonths - 1)
      : avgPerMonth;
    cumulative += newLeases;
    const vacant = totalUnits - cumulative;
    rows.push({
      month: constructionEndMonth + i,
      newLeases,
      totalLeases: cumulative,
      pctLeased: Math.round((cumulative / totalUnits) * 100),
      vacantUnits: vacant,
      pctVacant: Math.round((vacant / totalUnits) * 100),
    });
  }

  return rows;
}
